import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Notification from '../../core/Notification';
import Errored from '../../core/Errored';
import { makeCancelable } from '../../core/utils/utils';
import AreYouSure from '../../core/AreYouSure';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import BasicLanguageFieldDisplay from '../../core/BasicLanguageFieldDisplay';
import { v4 as uuidv4 } from 'uuid';
import GS from '../../grafsync/grafsync';
import FindPropertyDialog from './FindPropertyDialog';
import ResourceLink from '../../core/ResourceLink';
import TitledPanel from '../../core/TitledPanel';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from '../../core/style/gstheme';
import { MAX_TITLE_LENGTH } from '../../core/utils/constants';
import withGS from '../../core/withGShoc';
import SelectResourcesDialog from './SelectResourcesDialog';
import ShaclProperty from './ShaclProperty';
import { Typography } from '@material-ui/core';

const STANDARD_TARGETS = [
    "http://www.w3.org/2008/05/skos-xl#Label",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection"
];

class TemplateManager extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            areYouSure: false,
            associatedProperty: null,
            uniqueDomainList: [],
            scroll: false,
            displayTemplateIRI: ""
        };
        this.cardRef = React.createRef();
        this.cancellable = null;
    }

    componentDidMount() {
        this.collectTemplateData();
    }

    static getDerivedStateFromProps(props, state) {
        if (props.data._version !== state._version) {
            return {
                associatedProperty: null,
                isLoading: false,
                _version: props.data._version,
                displayTemplateIRI: props.displayTemplateIRI,
                ...(props.displayTemplateIRI !== state.displayTemplateIRI && props.displayTemplateIRI === props.id) && {
                    scroll: true
                }
            };
        }
        if (props.displayTemplateIRI === props.id && props.displayTemplateIRI !== state.displayTemplateIRI) {
            return {
                displayTemplateIRI: props.displayTemplateIRI,
                scroll: true
            };
        }
        if (props.displayTemplateIRI !== state.displayTemplateIRI) {
            return {
                displayTemplateIRI: props.displayTemplateIRI
            };
        }

        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.associatedProperty === null) {
            this.collectTemplateData();
        }
        if (this.state.scroll && this.cardRef && this.cardRef.current && !this.props.data.pendingDeletion) {
            this.cardRef.current.scrollIntoView({
                block: 'start',
                inline: 'center',
            });
            this.setState({ scroll: false });
        }
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
        return this.cancellable;
    }

    loadPathData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph, false));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    collectTemplateData() {
        if (this.state.isLoading) return;
        const { data } = this.props;
        if (!data.shProperty) return;
        this.setState({ isLoading: true }, async () => {
            try {
                await this.loadData(data.shProperty).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let pathList = [];
                data.shProperty.forEach(shProp => {
                    if (GS.isLoaded(shProp, this.props.graph)) {
                        const shPropData = GS.data(shProp, this.props.graph);
                        if (shPropData.shPath && !pathList.includes(shPropData.shPath)) {
                            pathList.push(shPropData.shPath);
                        }
                    }
                });
                const associatedProjectGraph = this.props.ontologyProjectGraph;
                await this.loadPathData(pathList, associatedProjectGraph).promise;
                pathList.sort((a, b) => {
                    const aData = GS.isLoaded(a, associatedProjectGraph) ? GS.data(a, associatedProjectGraph) : null;
                    const bData = GS.isLoaded(b, associatedProjectGraph) ? GS.data(b, associatedProjectGraph) : null;
                    const labelA = aData && aData.label[this.props.defaultLang] ? aData.label[this.props.defaultLang] : a;
                    const labelB = bData && bData.label[this.props.defaultLang] ? bData.label[this.props.defaultLang] : b;
                    if (labelA < labelB) return -1;
                    return 1;
                });
                let sortedPropList = pathList.map(prop => data.shProperty.find(sp => {
                    const spData = GS.data(sp, this.props.graph);
                    return spData.shPath === prop;
                }));
                let domainConflictList = [];
                // We check if the target classes match up with property domains
                if (data.shTargetClass && data.shTargetClass.length > 0) {
                    pathList.forEach(sp => {
                        const isLoaded = GS.isLoaded(sp, associatedProjectGraph);
                        if (!isLoaded) return;
                        const pData = GS.data(sp, associatedProjectGraph);
                        if (pData.domain && pData.domain.length > 0) {
                            const hasDirectClass = pData.domain.some(pd => data.shTargetClass.includes(pd));
                            // If there is a direct class no need to check subclasses
                            if (!hasDirectClass) {
                                const inferredResourceClasses = [].concat.apply([],
                                    pData.domain.map(classIRI => GS.getSubclasses(classIRI, associatedProjectGraph)));
                                const domainConflict = !inferredResourceClasses.some(irc => data.shTargetClass.includes(irc));
                                const label = pData.label && pData.label[this.props.defaultLang] ?
                                    pData.label[this.props.defaultLang]
                                    :
                                    pData.label && pData.label["und"] ?
                                        pData.label["und"]
                                        :
                                        pData.id;
                                if (domainConflict) domainConflictList.push(label);
                            }
                        }
                    });
                }
                this.setState({ isLoading: false, associatedProperty: pathList, sortedPropList, domainConflictList });
            }
            catch (e) {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, isLoading: false });
            }
        });
    }

    deletePropertyShape = (iri) => GS.deleteResource(iri, this.props.graph);

    onAddClass = (value) => {
        // Check not already added
        if (!this.props.data.shTargetClass || !this.props.data.shTargetClass.includes(value)) {
            this.props.onAdd("shTargetClass", value);
        }
    };

    onUpdate = () => (value) => this.props.onUpdate("label", value, this.props.defaultLang);

    onAddPropertyShape = (value) => {
        let newAPs = this.state.associatedProperty ?
            [...this.state.associatedProperty]
            :
            [];
        // Just in case already added
        if (newAPs.includes(value)) return;
        newAPs.push(value);
        this.setState({ associatedProperty: newAPs }, async () => {
            const shapeIRI = "https://grafsync.graphifi.com/data/shape/" + uuidv4();
            let newShape = {
                id: shapeIRI,
                type: "PropertyShape",
                shPath: value
            };
            await GS.insertResource(newShape, shapeIRI, this.props.graph);
            this.props.onAdd("shProperty", shapeIRI);
        });
    };

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteTemplate(this.props.id);
    };

    handleTargetChange = (targetClass) => {
        if (this.props.data.shTargetClass && this.props.data.shTargetClass.includes(targetClass)) {
            this.props.onDelete("shTargetClass", targetClass);
        }
        else {
            this.onAddClass(targetClass);
        }
    };

    onNotSure = () => this.setState({ areYouSure: false });

    render() {

        const { classes, graph, data, id, project, defaultLang, ontologyProjectGraph, isReadOnly, skosXL,
            allowedClasses, ontologyProject, buildLink } = this.props;
        const { associatedProperty, areYouSure, isErrored, sortedPropList, domainConflictList } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        const selectableClasses = allowedClasses ?
            allowedClasses.filter(ac => !data.shTargetClass || !data.shTargetClass.includes(ac))
            :
            [];
        let shaclProperties = data.shProperty ?
            sortedPropList
            :
            [];

        const usingUnavailableClasses = data.shTargetClass && data.shTargetClass
            .filter(tc => !STANDARD_TARGETS.includes(tc))
            .some(tc => !allowedClasses.includes(tc));

        const templateTestName = data.label[defaultLang] ?
            data.label[defaultLang].replace(/[^a-zA-Z0-9]/g, "")
            :
            data.id;

        return (
            <div data-test={"template-" + templateTestName} ref={this.cardRef}>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginTop: '10px', marginBottom: 12 }}>
                    <div data-test="template-title-text" style={{ flex: 1 }}>
                        <Typography className={classes.subMainHeading} style={{ marginTop: 12 }}>{data.label[defaultLang]}</Typography>
                    </div>
                    {!isReadOnly && <Tooltip placement="right" title='Delete this template' style={{ alignSelf: 'flex-end' }}>
                        <IconButton size="small" data-test="delete-template" name="deleteButton" onClick={this.checkIfSure} className={classes.deleteButton}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    }
                </div>
                <form autoComplete="off" noValidate="novalidate">
                    <BasicLanguageFieldDisplay
                        dataTest="template-title"
                        defaultLang={defaultLang}
                        title="Template Title"
                        property="label"
                        values={data.label}
                        onePerLang={true}
                        placeholder="Enter title text"
                        name="Template Title"
                        projectLang={[defaultLang]}
                        required={true}
                        maxLength={MAX_TITLE_LENGTH}
                        onUpdate={this.onUpdate}
                        onDelete={() => { }}
                        isReadOnly={isReadOnly} />
                </form>
                <TitledPanel margin={true} dataTest="template-classes" height="full" title="Target Classes" actions={
                    !isReadOnly && selectableClasses.length > 0 ?
                        <SelectResourcesDialog
                            title="Add Template Classes"
                            buttonTitle='Add template classes'
                            setHeight={false}
                            resources={selectableClasses}
                            addResource={this.onAddClass}
                            graph={ontologyProjectGraph}
                            defaultLang={defaultLang} />
                        :
                        null
                }>
                    {usingUnavailableClasses &&
                        <div style={{ marginBottom: 12 }} data-test="unavailable-classes-warning">
                            <Notification
                                error={false}
                                text="This template is using classes that are not currently available for a user to select."
                                dataTest="class-not-available-warning" />
                        </div>
                    }
                    <div>
                        {data.shTargetClass && data.shTargetClass.length > 0 ?
                            [...data.shTargetClass]
                                .filter(tc => !STANDARD_TARGETS.includes(tc))
                                .sort().map((ac, index) =>
                                    <ResourceLink
                                        dataTest={"class-resource-link-" + index}
                                        key={ac}
                                        id={ac}
                                        checkIfUsedOnDelete={
                                            {
                                                graph,
                                                value: ac,
                                                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                                message: [
                                                    "This associated class is used within this project.",
                                                    "Removing it from this template will not remove data using it.",
                                                    "Do you want to proceed with removing this class?"
                                                ]
                                            }
                                        }
                                        link={buildLink}
                                        handleNotAvailable={true}
                                        isReadOnly={isReadOnly}
                                        projectGraph={graph}
                                        graph={ontologyProjectGraph}
                                        property="shTargetClass"
                                        source={data.id}
                                        viewLang={defaultLang}
                                        defaultLang={defaultLang} />
                                )
                            :
                            <Typography paragraph data-test="no-classes-selected" variant="body2">Select classes that you want this template to display for. Classes can only be chosen from those made available for taxonomies in the list above and from the checkboxes below. <span style={{ fontWeight: 500 }} data-test="no-classes-selected" variant="body2">As no classes are selected this template will display in all places where it is used (except components).</span></Typography>
                        }
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        {skosXL &&
                            <FormControlLabel
                                data-test="option-label"
                                control={
                                    <Checkbox
                                        size="small"
                                        color="primary"
                                        disabled={isReadOnly}
                                        name="allowXL"
                                        className={classes.checked}
                                        checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2008/05/skos-xl#Label") : false}
                                        onClick={() => this.handleTargetChange("http://www.w3.org/2008/05/skos-xl#Label")} />
                                }
                                label="SKOS-XL Labels"
                                classes={{ label: classes.formLabelInline }}
                            />
                        }
                        <FormControlLabel
                            data-test="option-taxonomy"
                            control={
                                <Checkbox
                                    size="small"
                                    color="primary"
                                    disabled={isReadOnly}
                                    name="allowXL"
                                    className={classes.checked}
                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#ConceptScheme") : false}
                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#ConceptScheme")} />
                            }
                            label="Taxonomies"
                            classes={{ label: classes.formLabelInline }}
                        />
                        <FormControlLabel
                            data-test="option-collections"
                            control={
                                <Checkbox
                                    size="small"
                                    color="primary"
                                    disabled={isReadOnly}
                                    name="allowXL"
                                    className={classes.checked}
                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#Collection") : false}
                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#Collection")} />
                            }
                            label="Collections"
                            classes={{ label: classes.formLabelInline }}
                        />
                        <FormControlLabel
                            data-test="option-ordered-collections"
                            control={
                                <Checkbox
                                    size="small"
                                    color="primary"
                                    disabled={isReadOnly}
                                    name="allowXL"
                                    className={classes.checked}
                                    checked={data.shTargetClass ? data.shTargetClass.includes("http://www.w3.org/2004/02/skos/core#OrderedCollection") : false}
                                    onClick={() => this.handleTargetChange("http://www.w3.org/2004/02/skos/core#OrderedCollection")} />
                            }
                            label="Ordered Collections"
                            classes={{ label: classes.formLabelInline }}
                        />
                    </div>
                </TitledPanel>
                {domainConflictList && domainConflictList.length > 0 &&
                    <div style={{ marginTop: 12, marginBottom: 6 }} data-test="domain-conflict-warning">
                        <Notification
                            error={false}
                            text={"This template has properties with domains that are not a class or superclass of those listed. This is not necessarily an error. The properties are: "
                                + domainConflictList.sort().join(", ")}
                            dataTest="domain-issue-warning" />
                    </div>
                }
                <TitledPanel margin dataTest="template-properties" height="full" title="Properties to Display" actions={
                    !isReadOnly ?
                        <FindPropertyDialog
                            defaultLang={defaultLang}
                            projects={[ontologyProject, project]}
                            exclude={associatedProperty}
                            excludeByPath={[
                                {
                                    predicate: [
                                        "http://www.w3.org/ns/shacl#property",
                                        "http://www.w3.org/ns/shacl#path"
                                    ],
                                    target: id,
                                    targetGraph: graph,
                                    reverse: true
                                }
                            ]}
                            onAddProperty={this.onAddPropertyShape} />
                        :
                        null
                }>
                    {shaclProperties && shaclProperties.length > 0 &&
                        <div style={{ overflowX: 'scroll', overflowY: 'hidden', width: '100%' }}>
                            <Table style={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell style={{ minWidth: 200, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Name Override</Typography>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 200, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Description</Typography>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 80, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Min.<br />Count</Typography>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 80, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Max.<br />Count</Typography>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 100, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Max. Length</Typography>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 100, verticalAlign: 'bottom' }}>
                                            <Typography variant="body2">Pattern</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">Select From</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {shaclProperties.map((ap, index) =>
                                        <ShaclProperty
                                            dataTest={"prop-item-" + index}
                                            key={ap}
                                            id={ap}
                                            suppressLoading={true}
                                            onDeleteLink={this.deletePropertyShape}
                                            buildLink={buildLink}
                                            handleNotAvailable={true}
                                            isReadOnly={isReadOnly}
                                            graph={graph}
                                            project={project}
                                            ontologyProjectGraph={ontologyProjectGraph}
                                            defaultLang={defaultLang} />
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    }
                    {(!shaclProperties || shaclProperties.length === 0) &&
                        <React.Fragment>
                            <Typography paragraph data-test="no-properties-selected" variant="body2">Select properties that you want to be in this template.</Typography>
                        </React.Fragment>
                    }
                </TitledPanel>
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message="Do you really want to delete this template?" />}
            </div>
        );
    }
}

TemplateManager.defaultProps = {
    isReadOnly: false,
    skosXL: false
};

TemplateManager.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    displayTemplateIRI: PropTypes.string,
    buildLink: PropTypes.func.isRequired,
    allowedClasses: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    project: PropTypes.string.isRequired,
    skosXL: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onDeleteTemplate: PropTypes.func.isRequired,
    ontologyProject: PropTypes.string.isRequired,
    ontologyProjectGraph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(withGS(TemplateManager));